/**
 * Utility functions for working with files.
 */

import axios from 'axios';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { utils, write } from 'xlsx';

/**
 * Transforms a file in a data url (base64 encoded) string.
 *
 * @param accept file to be transformed
 * @param return promise to encoded string
 */
export const fileToDataUrl = async (file: File) => {
    const reader = new FileReader();
    const promise = new Promise(resolve => (reader.onloadend = resolve));
    reader.readAsDataURL(file);
    await promise;
    return reader.result as string;
};

/**
 * Filter a list of files leaving only files which format match the received `type`.
 *
 * @param files files to filter
 * @param type type regex
 * @returns filtered files
 */
export const fileFilter = (files: File[], type: RegExp) => [...files].filter(file => file.type.match(type));

/**
 * Utility for opening an file dialog.
 *
 * @param accept selectable file types, it can be '*', an extension (.doc, .pdf) or a media type (image/*, audio/*)
 * @param return promise to selected files
 */
export const openFileDialog = async (accept: string) => {
    const input = document.createElement('input');
    input.style.display = 'none';
    document.body.append(input);
    input.setAttribute('data-testid', 'uploader');
    input.type = 'file';
    input.accept = accept;
    const promise = new Promise<void>(
        resolve =>
            (input.onchange = document.documentElement.onmousemove = () => (
                (input.onchange = document.documentElement.onmousemove = null), resolve()
            )),
    );
    input.click();
    await promise;
    input.remove();
    return [...(input.files ?? [])];
};

/**
 * Utility function to download urls and data urls.
 * If it is a data url, it can be download as a normal file.
 * Common urls cannot be download like that because of cross origin issues, so a new tab is open with the url.
 *
 * @param data url or data url
 */
export const downloadFileDialog = async (data: string, filename = '') => {
    if (!data.startsWith('data:')) return void window.open(data, 'blank');
    const anchor = document.createElement('a');
    anchor.download = '';
    anchor.href = data;
    anchor.style.display = 'none';
    anchor.setAttribute('data-testid', 'download-anchor');
    document.body.append(anchor);
    anchor.click();
};

/**
 * Utility function that uploads file to GCS using the data urls
 * @param fileDataUrl
 */
export const uploadPicture = (fileDataUrl: string): Promise<string> => {
    const fileType = fileDataUrl.substring(fileDataUrl.indexOf(':') + 1, fileDataUrl.indexOf(';'));
    const fileExtension = fileType.split('/')[1];

    return fetch(fileDataUrl)
        .then(resp => resp.blob())
        .then(async blob => {
            const formData = new FormData();
            const fileName = uuidv4() + '.' + fileExtension;
            const fileData = new File([blob], fileName, { type: fileType });
            formData.append('file', fileData);
            formData.append('filename', fileName);
            const config = {
                headers: {
                    'content-type': 'multipart/form-data',
                },
            };
            const fileUpload = await axios.post('/images', formData, config);
            if (fileUpload.status === 200) {
                return fileName;
            }
            return '';
        })
        .catch(() => {
            return '';
        });
};

export type ContentUrl = {
    status: number;
    url: string;
};

export const getContentUrl = async (gcsFileName: string) => {
    const headerOptions = {
        params: {
            filename: gcsFileName,
        },
    };
    try {
        const response = await axios.post<ContentUrl>('/content-url', null, headerOptions);
        return response.status === 200 ? response.data.url : '';
    } catch (err) {
        return '';
    }
};

export type ExcelSheet = {
    name: string;
    data: unknown[];
};

export const exportToExcel = (sheets: ExcelSheet[], fileName: string): void => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const worksheets = sheets.reduce((o, sheet) => ({ ...o, [sheet.name]: utils.json_to_sheet(sheet.data) }), {});
    const workbook = { Sheets: worksheets, SheetNames: Object.keys(worksheets) };
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    saveAs(data, fileName + '.xlsx');
};
